<template>
  <div class="action-group">
    <!-- View Button -->
    <v-tooltip
      top
      v-if="showView"
      :open-delay="200"
      content-class="action-tooltip"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="action-btn action-view"
          :style="{ color: '#F59E0B', borderColor: '#e2e8f0' }"
          v-bind="attrs"
          v-on="on"
          @click.stop="$emit('view')"
          @mouseenter="hoverView = true"
          @mouseleave="hoverView = false"
        >
          <v-icon size="20" color="#F59E0B">mdi-eye-outline</v-icon>

          <!-- ✅ same hover bg like edit -->
          <div
            class="hover-bg"
            :style="{
              backgroundColor: hoverView
                ? 'rgba(245, 158, 11, 0.12)'
                : 'transparent',
            }"
          ></div>
        </button>
      </template>
      <span>{{ viewLabel }}</span>
    </v-tooltip>

    <!-- Accept Button -->
    <v-tooltip
      top
      v-if="showAccept"
      :open-delay="200"
      content-class="action-tooltip"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="action-btn action-accept"
          v-bind="attrs"
          v-on="on"
          :style="{ color: '#10B981', borderColor: '#e2e8f0' }"
          @click.stop="$emit('accept')"
          @mouseenter="hoverAccept = true"
          @mouseleave="hoverAccept = false"
        >
          <v-icon size="20" color="#10B981">mdi-check</v-icon>
          <div
            class="hover-bg bg-green"
            :style="{
              backgroundColor: hoverAccept
                ? 'rgba(16, 185, 129, 0.12)'
                : 'transparent',
            }"
          ></div>
        </button>
      </template>
      <span>{{ acceptLabel }}</span>
    </v-tooltip>

    <!-- Reject Button -->
    <v-tooltip
      top
      v-if="showReject"
      :open-delay="200"
      content-class="action-tooltip"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="action-btn action-reject"
          v-bind="attrs"
          v-on="on"
          @click.stop="$emit('reject')"
          @mouseenter="hoverReject = true"
          @mouseleave="hoverReject = false"
        >
          <v-icon size="20" color="#EF4444">mdi-close</v-icon>
          <div
            class="hover-bg bg-red"
            :style="{
              backgroundColor: hoverReject
                ? 'rgba(239, 68, 68, 0.12)'
                : 'transparent',
            }"
          ></div>
        </button>
      </template>
      <span>{{ rejectLabel }}</span>
    </v-tooltip>

    <!-- Edit Button -->
    <v-tooltip
      top
      v-if="showEdit"
      :open-delay="200"
      content-class="action-tooltip"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="action-btn action-edit"
          :style="{ color: '#3B82F6', borderColor: '#e2e8f0' }"
          v-bind="attrs"
          v-on="on"
          @click.stop="$emit('edit')"
          @mouseenter="hoverEdit = true"
          @mouseleave="hoverEdit = false"
        >
          <v-icon size="20" color="#3B82F6">mdi-pencil-outline</v-icon>
          <div
            class="hover-bg"
            :style="{
              backgroundColor: hoverEdit
                ? 'rgba(59, 130, 246, 0.12)'
                : 'transparent',
            }"
          ></div>
        </button>
      </template>
      <span>{{ editLabel }}</span>
    </v-tooltip>

    <!-- Delete Button -->
    <v-tooltip
      top
      v-if="showDelete"
      :open-delay="200"
      content-class="action-tooltip"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="action-btn action-delete"
          v-bind="attrs"
          v-on="on"
          @click.stop="$emit('delete')"
          @mouseenter="hoverDelete = true"
          @mouseleave="hoverDelete = false"
        >
          <v-icon size="20" color="#ef4444">mdi-trash-can-outline</v-icon>
          <div
            class="hover-bg bg-red"
            :style="{
              backgroundColor: hoverDelete
                ? 'rgba(239, 68, 68, 0.12)'
                : 'transparent',
            }"
          ></div>
        </button>
      </template>
      <span>{{ deleteLabel }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "ActionButtons",
  props: {
    showView: { type: Boolean, default: false },
    showEdit: { type: Boolean, default: false },
    showDelete: { type: Boolean, default: false },
    showAccept: { type: Boolean, default: false },
    showReject: { type: Boolean, default: false },
    viewLabel: { type: String, default: "View" },
    editLabel: { type: String, default: "Edit" },
    deleteLabel: { type: String, default: "Delete" },
    acceptLabel: { type: String, default: "Accept" },
    rejectLabel: { type: String, default: "Reject" },
  },
  data() {
    return {
      hoverEdit: false,
      hoverView: false,
      hoverDelete: false,
      hoverAccept: false,
      hoverReject: false,
    };
  },
  computed: {
    ...mapState("theme", ["primaryColor", "isDark"]),
    brandColor() {
      return this.primaryColor || "#6366f1";
    },
  },
  methods: {
    hexToRgba(hex, alpha) {
      if (!hex) return "rgba(99, 102, 241, 0.12)";
      let r = 0,
        g = 0,
        b = 0;
      if (hex.length === 4) {
        r = parseInt(hex[1] + hex[1], 16);
        g = parseInt(hex[2] + hex[2], 16);
        b = parseInt(hex[3] + hex[3], 16);
      } else if (hex.length === 7) {
        r = parseInt(hex.substring(1, 3), 16);
        g = parseInt(hex.substring(3, 5), 16);
        b = parseInt(hex.substring(5, 7), 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style scoped>
.action-group {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  align-items: center;
  white-space: nowrap;
  justify-content: flex-end;
}

.action-btn {
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* border: 1px solid #e2e8f0; */
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.action-btn .v-icon {
  z-index: 2;
}

.action-btn .hover-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  transition: all 0.2s ease;
  z-index: 1;
}

/* Delete Button */
.action-delete {
  border-color: #fca5a5;
}
.action-delete:hover {
  border-color: #ef4444;
}
.action-delete:hover .hover-bg.bg-red {
  background-color: rgba(239, 68, 68, 0.12);
}
</style>

<style>
.action-tooltip {
  background: #111827 !important;
  color: #fff !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  border-radius: 6px !important;
  padding: 4px 8px !important;
  text-transform: capitalize;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
